import { createContext, useContext, useState, useEffect } from 'react';
import apiClient from '../services/api';

const defaultTheme = {
  colors: {
    primary: '#0F7BC0',
    secondary: '#4CAF50',
    text_primary: '#1a1a1a',
    text_secondary: '#666666',
    text_on_primary: '#ffffff',
    background: '#F8FAFC',
    surface: '#FFFFFF',
    surface_secondary: '#7791A9',
    border: '#E2E8F0',
    success: '#4CAF50',
    warning: '#FF9800',
    error: '#EF4444',
    info: '#2196F3'
  }
};

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(defaultTheme);
  const [loading, setLoading] = useState(false);

  const loadTheme = async () => {
    // Only try to load theme if user is logged in
    const token = localStorage.getItem('access_token');
    if (!token) {
      applyThemeToDOM(defaultTheme.colors);
      setLoading(false);
      return;
    }

    try {
      const response = await apiClient.get('/themes/active');
      if (response.data.colors) {
        setTheme(response.data);
        applyThemeToDOM(response.data.colors);
      } else {
        applyThemeToDOM(defaultTheme.colors);
      }
    } catch (error) {
      console.warn('Could not load theme, using default:', error.message);
      // Use default theme on error (e.g., not logged in)
      applyThemeToDOM(defaultTheme.colors);
    } finally {
      setLoading(false);
    }
  };

  const applyThemeToDOM = (colors) => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--color-text-primary', colors.text_primary);
    root.style.setProperty('--color-text-secondary', colors.text_secondary);
    root.style.setProperty('--color-text-on-primary', colors.text_on_primary);
    root.style.setProperty('--color-background', colors.background);
    root.style.setProperty('--color-surface', colors.surface);
    root.style.setProperty('--color-surface-secondary', colors.surface_secondary);
    root.style.setProperty('--color-border', colors.border);
    root.style.setProperty('--color-success', colors.success);
    root.style.setProperty('--color-warning', colors.warning);
    root.style.setProperty('--color-error', colors.error);
    root.style.setProperty('--color-info', colors.info);
  };

  const updateTheme = async (newTheme) => {
    setTheme(newTheme);
    applyThemeToDOM(newTheme.colors);
  };

  useEffect(() => {
    // Apply default theme immediately
    applyThemeToDOM(defaultTheme.colors);
    
    // Then try to load user theme
    const loadUserTheme = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        return;
      }

      try {
        const response = await apiClient.get('/themes/active');
        if (response.data.colors) {
          setTheme(response.data);
          applyThemeToDOM(response.data.colors);
        }
      } catch (error) {
        console.warn('Could not load theme, using default:', error.message);
      }
    };
    
    loadUserTheme();
  }, []);

  const value = {
    theme,
    loading,
    updateTheme,
    reloadTheme: loadTheme
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};